import { Buffer } from 'buffer'

import _ from 'lodash'
import mixpanel from 'mixpanel-browser'
import smartlookClient from 'smartlook-client'

const guardedMixpanel = (() => {
  if (process.env.MIXPANEL_TOKEN) {
    return mixpanel
  }

  return {
    track: _.noop,
    track_forms: _.noop,
    identify: _.noop,
    get_distinct_id: _.noop,
    init: _.noop,
    register: _.noop,
    reset: _.noop,
  }
})()

const guardedSmartlook = (() => {
  if (process.env.SMARTLOOK_API_KEY) {
    return smartlookClient
  }

  return {
    init: _.noop,
    identify: _.noop,
  }
})()

const setMixpanelReferrerProperties = (location) => {
  if (!location) {
    return
  }

  const { referrerLabel, searchQuery } = calcMixpanelReferrerLabel(location)

  // super properties
  mixpanel.register({
    session_referrer_label:
      sessionStorage.getItem('session_referrer_label') || undefined,
    session_referrer_search_query:
      sessionStorage.getItem('session_referrer_search_query') || undefined,
    direct_referrer_label: referrerLabel,
    direct_referrer_search_query: searchQuery,
  })
}

// If it is not on fist page load and not on react router change, use this to get referrer props
// Do no use the calcMixpanelReferrerLabel that re-calculates everything from doument referrer as react router does not change the document referrer and this will cause wrong results
export const getMixpanelReferrerProperties = () => {
  try {
    if (document?.mixpanel_loaded) {
      return {
        session_referrer_label: mixpanel.get_property('session_referrer_label'),
        session_referrer_search_query: mixpanel.get_property(
          'session_referrer_search_query'
        ),
        direct_referrer_label: mixpanel.get_property('direct_referrer_label'),
        direct_referrer_search_query: mixpanel.get_property(
          'direct_referrer_search_query'
        ),
      }
    }

    const { referrerLabel, searchQuery } = calcMixpanelReferrerLabel(
      document?.referrer
    )

    return {
      session_referrer_label:
        sessionStorage.getItem('session_referrer_label') || undefined,
      session_referrer_search_query:
        sessionStorage.getItem('session_referrer_search_query') || undefined,
      direct_referrer_label: referrerLabel,
      direct_referrer_search_query: searchQuery,
    }
  } catch (error) {
    return {}
  }
}

export const getTrackingHeadersFromSearchString = (search) => {
  const encodedHeaders = new URLSearchParams(search)?.get('mp_headers')
  if (encodedHeaders) {
    try {
      return JSON.parse(Buffer.from(encodedHeaders ?? '', 'base64'))
    } catch {
      return {}
    }
  }
  return {}
}

const calcMixpanelReferrerLabel = (location) => {
  let referrerLabel = mapReferrerUrlToLabel(document.referrer)
  let searchQuery = null

  if (referrerLabel === 'theMednet Search') {
    const referrerUrl = new URL(document.referrer)
    searchQuery = referrerUrl.searchParams.get('query')
  }

  // Check that the referrer is the site itself, otherwise someone could have copied the link and sent it to another person
  if (referrerLabel === 'theMednet') {
    const currentUrlParams = new URLSearchParams(location.search)

    if (currentUrlParams.get('src') === 'popover-search') {
      referrerLabel = 'theMednet Popover Search'
      searchQuery = currentUrlParams.get('query')
    }
  }

  // I don't think it should happen that we get referrer label set to something other than other, direct or email when notification_source
  // value is set, but adding this condition in case
  if (
    (_.isEmpty(referrerLabel) ||
      ['Direct', 'Other', 'Email'].includes(referrerLabel)) &&
    !_.isEmpty(sessionStorage.getItem('notification_source'))
  ) {
    return {
      referrerLabel: sessionStorage.getItem('notification_source'),
      searchQuery: null,
    }
  }

  return {
    referrerLabel,
    searchQuery,
  }
}

const mapReferrerUrlToLabel = (referrerUrl) => {
  const referrerMap = {
    // put internal urls first, to skip checking all the list when moving to other pages in the site
    'theMednet Search': [/(the)?mednet\.(com|org)\/search\/siteSearch/i],

    theMednet: [
      /(the)?mednet\.(com|org)/i,
      /mednetassets\.(com|org)/i, // http://micro.production.mednetassets.com micro backend
    ],

    Email: [
      // https://stackoverflow.com/questions/41026078/what-is-com-google-android-gm
      /com\.google\.android\.gm/i, // Gmail app for android

      /^https?:\/\/(z|my|web|e)?mail.?\./i,
    ],

    'Google Search': [
      /www\.google\..*/i,

      // https://carloseo.com/what-is-com-google-android-googlequicksearchbox-referral-google-analytics/#:~:text=com.google.android.googlequicksearchbox%20is%20part%20of%20the%20Google,of%20naming%20apps%20in%20Android.
      /com\.google\.android\.googlequicksearchbox/i, // Quick Google search bar for android
    ],

    'Bing Search': [/www\.bing\.com/i],

    'Yahoo Search': [/search\.yahoo\.co(m)?/i],

    Facebook: [/facebook\.com/i],

    Twitter: [/^https?:\/\/t.co/i, /twitter\.com/i],

    LinkedIn: [/linkedin\.com/i],
  }

  if (_.isEmpty(referrerUrl)) {
    return 'Direct'
  }

  for (const [label, urls] of Object.entries(referrerMap)) {
    for (const url of urls) {
      if (url.test(referrerUrl)) {
        return label
      }
    }
  }

  return 'Other'
}

export {
  guardedMixpanel as mixpanel,
  guardedSmartlook as smartlookClient,
  calcMixpanelReferrerLabel,
  setMixpanelReferrerProperties,
}
